import type {
  AsyncLocalStorage as NodeAsyncLocalStorage,
  AsyncLocalStorage as _AsyncLocalStorageType,
} from "node:async_hooks";
import type * as NodeAsyncHooks from "node:async_hooks";

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  requestId?: string;
  [key: string]: unknown;
}

let currentRequestId: string | undefined;

// 2026-08-30 fix P1-2: AsyncLocalStorage for concurrent serverless requests (Vercel Node 24 reuse)
// Global fallback kept for test/compat; ALS is source of truth when available
let requestStore: NodeAsyncLocalStorage<{ requestId: string }> | null = null;
try {
  const { AsyncLocalStorage } =
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    require("node:async_hooks") as typeof NodeAsyncHooks;
  requestStore = new AsyncLocalStorage();
} catch {
  requestStore = null;
}

export function setRequestId(id: string) {
  currentRequestId = id;
  if (requestStore) {
    // enterWith keeps id for the current async chain without needing explicit run() wrapper
    try {
      const store = requestStore.getStore();
      if (store) store.requestId = id;
      else requestStore.enterWith({ requestId: id });
    } catch {}
  }
}

export function getRequestId(): string | undefined {
  if (requestStore) {
    const store = requestStore.getStore();
    if (store?.requestId) return store.requestId;
  }
  return currentRequestId;
}

/**
 * 2026-09-07 Sentry observability: el proxy genera X-Request-Id por request
 * (crypto.randomUUID) y lo propaga como request header, pero cada route handler
 * corre en su propia invocación — el AsyncLocalStorage del proxy no cruza.
 * Cada ruta caliente debe llamar a esto al inicio para que logger incluya
 * requestId y los logs correlacionen con el header X-Request-Id de respuesta.
 * Fail-open: si no hay header válido se genera uno local (nunca lanza).
 *
 * 2026-10-11 uuid-validation (research 2026: RFC 9562 + CWE-117 log-forging —
 * un X-Request-Id forjado (`../../`, 64×`A`, secuencias de escape) contaminaba
 * requestId en logs y el header de respuesta): SOLO se adopta el entrante si
 * es UUID válido (lo que el proxy genera con crypto.randomUUID). Si no, se
 * genera uno nuevo y el entrante se conserva (truncado 64) como
 * clientRequestId — trazabilidad del caller sin contaminar el correlador.
 * setRequestId() NO valida a propósito (uso interno + tests con IDs fijos).
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let lastClientRequestId: string | undefined;

/** Entrante no-UUID del último bind (trazabilidad del caller, nunca correlador). */
export function getClientRequestId(): string | undefined {
  return lastClientRequestId;
}

export function bindRequestId(
  headers: Pick<Headers, "get"> | { get(name: string): string | null },
): string {
  let incoming: string | null = null;
  try {
    incoming = headers.get("x-request-id") ?? headers.get("X-Request-Id");
  } catch {
    incoming = null;
  }
  const clean = (incoming ?? "").trim().slice(0, 64);
  if (UUID_RE.test(clean)) {
    lastClientRequestId = undefined;
    setRequestId(clean);
    return clean;
  }
  lastClientRequestId = clean || undefined;
  const reuse = currentRequestId ?? "";
  if (reuse && UUID_RE.test(reuse)) {
    return reuse;
  }
  let id: string;
  try {
    id = crypto.randomUUID();
  } catch {
    id = `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  }
  setRequestId(id);
  return id;
}

// 2026-09-01 PII filter — Sentry Gaps 2026 + Nurbak 2026 (Sentry único, external webhook removed 2026-09-01)
// Never log email/base64/text full — GDPR — redact before JSON.stringify
// 2026-10-11 SSOT: sanitizeString/sanitizeValue se EXPORTAN y son la única
// implementación — src/lib/client-error-reporting.ts los importa en vez de
// duplicarlos (antes divergían: al cliente le faltaba PHONE_RE y el always-
// secret por keyHint). Un solo fix de PII cubre servidor + cliente.
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const LONG_BASE64_RE = /[A-Za-z0-9+/]{40,}={0,2}/g;
const REPEATED_A_RE = /A{10,}/g;
const JWT_RE =
  /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;
// 2026-09-24 FASE 4 PII: telefono es dato personal (GDPR art.17) — patron
// conservador 10 digitos, paridad con image-generation.ts:488.
const PHONE_RE = /\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/g;

export function sanitizeString(value: string): string {
  let out = value;
  out = out.replace(EMAIL_RE, "[email-redacted]");
  out = out.replace(PHONE_RE, "[phone-redacted]");
  out = out.replace(LONG_BASE64_RE, (m) =>
    m.length > 40 ? "[base64-redacted]" : m,
  );
  out = out.replace(REPEATED_A_RE, "[base64-redacted]");
  out = out.replace(JWT_RE, "[token-redacted]");
  out = out.replace(/\bBearer\s+[A-Za-z0-9\-._~+/]+=*/gi, "Bearer [redacted]");
  if (out.length > 2000) out = out.slice(0, 2000) + "…[truncated]";
  return out;
}

export function sanitizeValue(value: unknown, keyHint?: string): unknown {
  if (typeof value === "string") {
    const lowerKey = (keyHint ?? "").toLowerCase();
    const isAlwaysSecret =
      lowerKey.includes("password") ||
      lowerKey.includes("secret") ||
      lowerKey.includes("token") ||
      lowerKey.includes("apikey") ||
      lowerKey.includes("api_key") ||
      lowerKey.includes("api-key") ||
      lowerKey.includes("authorization") ||
      lowerKey.includes("auth");
    if (isAlwaysSecret) {
      if (value.length > 40)
        return "[redacted:" + lowerKey + ":" + value.length + "]";
      return "[redacted:" + lowerKey + "]";
    }
    if (
      lowerKey.includes("base64") ||
      lowerKey.includes("text") ||
      lowerKey.includes("extracted") ||
      lowerKey.includes("email") ||
      lowerKey.includes("phone")
    ) {
      if (value.length > 40)
        return "[redacted:" + lowerKey + ":" + value.length + "]";
      return sanitizeString(value);
    }
    return sanitizeString(value);
  }
  if (Array.isArray(value)) return value.map((v) => sanitizeValue(v));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>))
      out[k] = sanitizeValue(v, k);
    return out;
  }
  return value;
}

function formatLog(
  level: LogLevel,
  message: string,
  meta?: Record<string, unknown>,
): string {
  const rid = getRequestId();
  const sanitizedMessage = sanitizeString(message);
  const sanitizedMeta = meta
    ? (sanitizeValue(meta) as Record<string, unknown>)
    : undefined;
  const entry: LogEntry = {
    level,
    message: sanitizedMessage,
    timestamp: new Date().toISOString(),
    ...(rid ? { requestId: rid } : {}),
    ...(sanitizedMeta ?? {}),
  };
  return JSON.stringify(entry);
}

const BUSINESS_INFO_PREFIXES = [
  "subscription-",
  "payment-source-",
  "subscriptionrenewed",
  "subscriptionrenewal",
];

const BUSINESS_INFO_EXACT = new Set([
  "subscription-renewed",
  "subscription-renewed-inline",
  "subscription-cancelled",
  "subscription-reactivated",
  "payment-source-updated",
  "plan_changed",
]);

function isBusinessInfoEvent(message: string): boolean {
  if (BUSINESS_INFO_EXACT.has(message)) return true;
  const lower = message.toLowerCase();
  for (const prefix of BUSINESS_INFO_PREFIXES) {
    if (lower.startsWith(prefix)) return true;
  }
  // Explicit business events that may contain additional context prefixes
  if (
    lower.includes("subscription-renewed") ||
    lower.includes("subscription-cancelled")
  )
    return true;
  return false;
}

// 2026-08-23 logger — pino-style, requestId, production safe
// 2026-09-14 no-console: este archivo es el ÚNICO dueño del transporte
// console en src (patrón "single owner" como Cache-Control en la ruta).
// El allowlist warn/error de eslint rige para el resto del app-code; aquí los
// eslint-disable son intencionales y acotados: debug va gated a development,
// info va muestreado al 1% en producción (eventos de negocio al 100%), y los
// niveles console.debug/info coinciden con el campo `level` del JSON para que
// los drains de Vercel clasifiquen bien. logger.test.ts:22-49 fija ambos
// transportes — no migrar a warn/error sin actualizar esos tests y los
// dashboards que filtran por nivel.
export const logger = {
  debug(message: string, meta?: Record<string, unknown>) {
    // Gate dev (AGENTS.md Free-tier Caps): cero console.debug en prod.
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console -- transporte único, ver arriba
      console.debug(formatLog("debug", message, meta));
    }
  },
  info(message: string, meta?: Record<string, unknown>) {
    // Business events (subscription-renewed, plan_changed, etc.) are always sampled (100%).
    // Only noisy events (chat-queue, keep-alive, etc.) remain at 1% in production.
    if (
      process.env.NODE_ENV === "production" &&
      !isBusinessInfoEvent(message) &&
      Math.random() >= 0.01
    )
      return;
    // eslint-disable-next-line no-console -- transporte único, ver arriba
    console.info(formatLog("info", message, meta));
  },
  warn(message: string, meta?: Record<string, unknown>) {
    console.warn(formatLog("warn", message, meta));
  },
  error(message: string, meta?: Record<string, unknown>) {
    console.error(formatLog("error", message, meta));
  },
};

/**
 * 2026-09-24 FASE 4G MEJORA transversal [P3] label de latencia (research 2026:
 * Vercel changelog 2026-08-10 "Server-Timing response headers will pass
 * through to the client" + Vercel Observability docs 2026 "Debug errors and
 * latency with Observability — logs + traces + metrics" + Next.js #62353
 * Server-Timing: el header `Server-Timing: proxy;dur=…` ya viaja (AGENTS.md),
 * pero ningún helper central formatea duraciones — cada superficie
 * concatenaba `${ms}ms` (65_000ms ilegible, NaN corrompe el header).
 * Formato: <1000ms → "850ms", ≥1000ms → segundos con 1 decimal ("1.2s",
 * "65.0s"). Invalido/negativo → "0ms" fail-closed (header nunca NaN).
 * Puro, sin copy traducible (unidad SI), sin visual, Lovable intacto.
 * https://vercel.com/changelog/server-timing-header
 * https://vercel.com/docs/observability
 */
export function formatLatencyMs(ms: number): string {
  const safe = Number.isFinite(ms) ? Math.max(0, ms) : 0;
  if (safe < 1000) return `${Math.round(safe)}ms`;
  return `${(safe / 1000).toFixed(1)}s`;
}
